package com.example.mystybag.login.views.views

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import kotlinx.coroutines.delay
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.CheckBoxLink
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.common.views.CommonBottomSheetFactory
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.model.LinkModel
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import com.example.mystybag.login.logic.MerchantRegistrationAddCompanyDetailsViewModel
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.common.extensions.keyboardPadding

@Composable
fun MerchantRegistrationAddCompanyDetailsView(viewModel: MerchantRegistrationAddCompanyDetailsViewModel) {
    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    if (PlatformHelper.isWeb) {
        MerchantRegistrationAddCompanyDetailsWebView(viewModel)
        return
    } else {
        BottomSheetUIView(
            modifier = Modifier.fillMaxSize(),
            viewModel = viewModel,
            bottomSheetFactory = { CommonBottomSheetFactory(it) }
        ) { _ ->
            MerchantRegistrationAddCompanyDetailsContent(viewModel)
        }
    }
}

/** Native (Android / iOS): progress + BULSTAT scroll; checkbox and primary action pinned to the bottom. */
@Composable
internal fun MerchantRegistrationAddCompanyDetailsContent(
    viewModel: MerchantRegistrationAddCompanyDetailsViewModel
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .fillMaxWidth()
            .keyboardPadding()
            .padding(bottom = p8)
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .fillMaxHeight()
                .verticalScroll(rememberScrollState())
        ) {
            MerchantRegistrationAddCompanyDetailsTopSection(
                viewModel = viewModel,
                includeSpacerAfterBulstat = true
            )
        }
        MerchantRegistrationAddCompanyDetailsFooterSection(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyDetailsTopSection(
    viewModel: MerchantRegistrationAddCompanyDetailsViewModel,
    includeSpacerAfterBulstat: Boolean
) {
    val uiState by viewModel.uiState.subscribeAsState()
    MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.COMPANY_IDENTIFIER)

    Spacer(modifier = Modifier.height(p36))

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Texts.DisplaySmall(
            text = stringResource(SR.strings.company_identifier),
            modifier = Modifier.fillMaxWidth().padding(horizontal = p16),
            textAlign = TextAlign.Start
        )
    }

    Spacer(modifier = Modifier.height(p24))

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        DefaultCircleShapedTextField(
            value = uiState.bulstat,
            onValueChange = viewModel::updateBulstat,
            label = stringResource(SR.strings.bulstat),
            modifier = Modifier.fillMaxWidth(),
            errorText = uiState.bulstatError ?: "",
            isError = uiState.bulstatError != null,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
        )
    }

    if (includeSpacerAfterBulstat) {
        Spacer(modifier = Modifier.height(p16))
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyDetailsFooterSection(
    viewModel: MerchantRegistrationAddCompanyDetailsViewModel
) {
    val uiState by viewModel.uiState.subscribeAsState()
    Column(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16)
        ) {
            val prefix = stringResource(SR.strings.terms_agreement_prefix)
            val privacyPolicyText = stringResource(SR.strings.privacy_policy)
            val andText = stringResource(SR.strings.terms_agreement_and)
            val linkText = stringResource(SR.strings.general_terms_mystybag)
            val privacyPolicyUrl = "https://$DOMAIN/static/documents/privacyPolicy.pdf"
            val fullLinkText = "$privacyPolicyText $andText $linkText"
            val linkModels = remember(prefix, fullLinkText) {
                listOf(
                    LinkModel("$prefix ", ""),
                    LinkModel(fullLinkText, privacyPolicyUrl)
                )
            }
            CheckBoxLink(
                texts = linkModels,
                checked = uiState.privacyPolicyAccepted,
                onCheckBoxClick = { viewModel.toggleTermsAccepted() },
                onTextClick = { index ->
                    val linkModel = linkModels[index]
                    if (linkModel.link.isNotEmpty()) {
                        com.example.mystybag.core.expected.openInExternalBrowser(linkModel.link)
                    }
                }
            )
            if (uiState.privacyPolicyError) {
                Spacer(modifier = Modifier.height(p8))
                Texts.BodySmall(
                    text = stringResource(SR.strings.please_enter_all_required_fields),
                    color = AppTheme.current.colors.error,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }

        Spacer(modifier = Modifier.height(p32))

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16),
            type = DefaultButtonType.PRIMARY,
            caption = stringResource(SR.strings.next),
            callback = viewModel::onContinue,
            enabled = !uiState.isLoading
        )
    }
}
